/**
 * Systeme d'ancrage — positionnement relatif des pieces 3D
 *
 * REFERENTIEL : ORIGINE DXF
 * - Le pivot local (0,0,0) correspond a l'origine du DXF
 * - Les ancrages sont des fractions [-0.5, +0.5] de la bbox, appliquees par rapport a l'origine DXF
 * - On utilise les VRAIES dimensions du mesh (mesh.userData.realSize)
 *
 * Convention Three.js :
 *   X = Largeur (UI "Y")   Y = Hauteur (UI "Z")   Z = Profondeur (UI "X")
 *
 * SYNC avec usipanel-backoffice/lib/anchorSystem.js
 * Spécificité renderer : les IDs sont systematiquement convertis en String pour
 * etre robustes aux ID retournes par le backend public (number vs string).
 */

import * as THREE from 'three';

// =============================================================================
// POINTS D'ANCRAGE — fractions [-0.5, +0.5] par rapport a l'origine DXF
// =============================================================================
// Le pivot (0,0,0) est a l'ORIGINE DU DXF.
// Les ancrages sont des fractions de la bbox, appliquees par rapport a l'origine DXF.

export const ANCHOR_POINTS = {
  T_FL: { name: 'Haut Avant-Gauche',   position: [-0.5,  0.5,  0.5] },
  T_FC: { name: 'Haut Avant-Centre',   position: [ 0,    0.5,  0.5] },
  T_FR: { name: 'Haut Avant-Droite',   position: [ 0.5,  0.5,  0.5] },
  T_ML: { name: 'Haut Milieu-Gauche',  position: [-0.5,  0.5,  0  ] },
  T_C:  { name: 'Haut Centre',         position: [ 0,    0.5,  0  ] },
  T_MR: { name: 'Haut Milieu-Droite',  position: [ 0.5,  0.5,  0  ] },
  T_BL: { name: 'Haut Arriere-Gauche', position: [-0.5,  0.5, -0.5] },
  T_BC: { name: 'Haut Arriere-Centre', position: [ 0,    0.5, -0.5] },
  T_BR: { name: 'Haut Arriere-Droite', position: [ 0.5,  0.5, -0.5] },

  B_FL: { name: 'Bas Avant-Gauche',    position: [-0.5, -0.5,  0.5] },
  B_FC: { name: 'Bas Avant-Centre',    position: [ 0,   -0.5,  0.5] },
  B_FR: { name: 'Bas Avant-Droite',    position: [ 0.5, -0.5,  0.5] },
  B_ML: { name: 'Bas Milieu-Gauche',   position: [-0.5, -0.5,  0  ] },
  B_C:  { name: 'Bas Centre',          position: [ 0,   -0.5,  0  ] },
  B_MR: { name: 'Bas Milieu-Droite',   position: [ 0.5, -0.5,  0  ] },
  B_BL: { name: 'Bas Arriere-Gauche',  position: [-0.5, -0.5, -0.5] },
  B_BC: { name: 'Bas Arriere-Centre',  position: [ 0,   -0.5, -0.5] },
  B_BR: { name: 'Bas Arriere-Droite',  position: [ 0.5, -0.5, -0.5] },

  M_FL: { name: 'Milieu Avant-Gauche', position: [-0.5, 0,  0.5] },
  M_FR: { name: 'Milieu Avant-Droite', position: [ 0.5, 0,  0.5] },
  M_BL: { name: 'Milieu Arriere-Gauche', position: [-0.5, 0, -0.5] },
  M_BR: { name: 'Milieu Arriere-Droite', position: [ 0.5, 0, -0.5] },

  F_C:  { name: 'Avant Centre',    position: [ 0,    0,  0.5] },
  K_C:  { name: 'Arriere Centre',  position: [ 0,    0, -0.5] },
  L_C:  { name: 'Gauche Centre',   position: [-0.5,  0,  0  ] },
  R_C:  { name: 'Droite Centre',   position: [ 0.5,  0,  0  ] },

  CENTER: { name: 'Centre',        position: [ 0, 0, 0 ] },
};

export const ANCHOR_CATEGORIES = {
  'Dessus (Z+)':       ['T_FL','T_FC','T_FR','T_ML','T_C','T_MR','T_BL','T_BC','T_BR'],
  'Dessous (Z-)':      ['B_FL','B_FC','B_FR','B_ML','B_C','B_MR','B_BL','B_BC','B_BR'],
  'Aretes verticales': ['M_FL','M_FR','M_BL','M_BR'],
  'Faces laterales':   ['F_C','K_C','L_C','R_C'],
  'Centre':            ['CENTER'],
};

const LEGACY_MAP = {
  FTL:'T_FL', FTC:'T_FC', FTR:'T_FR', FML:'M_FL', FC:'F_C', FMR:'M_FR',
  FBL:'B_FL', FBC:'B_FC', FBR:'B_FR', BTL:'T_BL', BTC:'T_BC', BTR:'T_BR',
  BML:'M_BL', BC:'K_C', BMR:'M_BR', BBL:'B_BL', BBC:'B_BC', BBR:'B_BR',
  CENTER:'CENTER',
};
export function convertLegacyAnchorId(id) {
  return ANCHOR_POINTS[id] ? id : (LEGACY_MAP[id] || id);
}

// =============================================================================
// DIMENSIONS D'UNE FACE — calculees depuis les dimensions produit
// =============================================================================

/** Epaisseur fixe d'un panneau en mm */
export const PANEL_DEPTH = 3;

/**
 * Calcule les dimensions THEORIQUES d'une face a partir des dimensions produit.
 * NOTE: Cette fonction est un FALLBACK. On prefere utiliser les VRAIES dimensions
 * du mesh (mesh.userData.realSize) quand elles sont disponibles.
 */
export function getFaceDimensions(part, productSize) {
  const axX = (part.resizeAxisX || 'y').replace('-', '');
  const axY = (part.resizeAxisY || 'z').replace('-', '');
  return {
    width:  productSize[axX] || 100,
    height: productSize[axY] || 100,
    depth:  PANEL_DEPTH,
  };
}

// =============================================================================
// BBOX EN IGNORANT LES FLAPS DE PLIURES "rectangular_cutout"
// =============================================================================

/**
 * Calcule la bbox d'une scene (ou d'un Object3D) en excluant les sous-arbres
 * tagges `userData.isCutoutFlap = true` (flaps des pliures rectangular_cutout).
 * Les flaps `edge_split` (pliures classiques) restent inclus.
 *
 * Sert a calculer les dimensions servant au systeme d'ancrage : le panneau
 * parent + ses bords plies sont la reference, pas les volets interieurs leves.
 */
export function computeBBoxExcludingCutoutFlaps(scene) {
  const box = new THREE.Box3();
  let hasAny = false;
  let skipped = 0;
  scene.traverse((obj) => {
    if (!obj.isMesh || !obj.geometry) return;
    let cur = obj;
    while (cur) {
      if (cur.userData && cur.userData.isCutoutFlap) {
        skipped++;
        return;
      }
      cur = cur.parent;
    }
    obj.updateMatrixWorld();
    obj.geometry.computeBoundingBox();
    if (obj.geometry.boundingBox) {
      const meshBox = obj.geometry.boundingBox.clone().applyMatrix4(obj.matrixWorld);
      box.union(meshBox);
      hasAny = true;
    }
  });
  if (!hasAny) box.makeEmpty();
  return box;
}

// =============================================================================
// AABB apres rotation — toujours centree
// =============================================================================

export function rotatePoint({ x, y, z }, rot) {
  const rx = (rot.x || 0) * Math.PI / 180;
  const ry = (rot.y || 0) * Math.PI / 180;
  const rz = (rot.z || 0) * Math.PI / 180;

  // Ordre Three.js Euler 'XYZ' : matrice Rx * Ry * Rz
  // En application sequentielle : d'abord Rz, puis Ry, puis Rx
  if (rz) { const c = Math.cos(rz), s = Math.sin(rz); [x, y] = [x*c - y*s, x*s + y*c]; }
  if (ry) { const c = Math.cos(ry), s = Math.sin(ry); [x, z] = [x*c + z*s, -x*s + z*c]; }
  if (rx) { const c = Math.cos(rx), s = Math.sin(rx); [y, z] = [y*c - z*s, y*s + z*c]; }

  return { x, y, z };
}

/**
 * AABB apres rotation Euler XYZ, par rapport a l'origine DXF.
 */
export function getWorldAABB(dims, rotation = { x: 0, y: 0, z: 0 }) {
  const hw = (dims.width  || 0) / 2;
  const hh = (dims.height || 0) / 2;
  const hd = (dims.depth  || 0) / 2;

  let minX = Infinity, minY = Infinity, minZ = Infinity;
  let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;

  for (let sx = -1; sx <= 1; sx += 2)
    for (let sy = -1; sy <= 1; sy += 2)
      for (let sz = -1; sz <= 1; sz += 2) {
        const r = rotatePoint({ x: sx * hw, y: sy * hh, z: sz * hd }, rotation);
        if (r.x < minX) minX = r.x; if (r.x > maxX) maxX = r.x;
        if (r.y < minY) minY = r.y; if (r.y > maxY) maxY = r.y;
        if (r.z < minZ) minZ = r.z; if (r.z > maxZ) maxZ = r.z;
      }

  return { width: maxX - minX, height: maxY - minY, depth: maxZ - minZ };
}

// =============================================================================
// POSITION D'UN POINT D'ANCRAGE
// =============================================================================

/**
 * Position monde d'un point d'ancrage :
 * 1. Calcul du point dans l'espace LOCAL = bboxCenter + fraction * dimension
 *    (les fractions [-0.5, +0.5] sont relatives au centre de la bbox,
 *     et bboxCenter est l'offset entre l'origine DXF et le centre de la bbox)
 * 2. Application de la rotation à ce point
 * 3. Addition de la position du pivot (origine DXF dans l'espace monde)
 */
export function getAnchorPosition(pointId, faceDims, pivotPos, rotation) {
  const id = convertLegacyAnchorId(pointId);
  const anchor = ANCHOR_POINTS[id];
  if (!anchor) { console.warn(`Ancrage inconnu: ${pointId}`); return { ...pivotPos }; }

  const [ax, ay, az] = anchor.position;
  const bc = faceDims.bboxCenter || { x: 0, y: 0, z: 0 };
  const localPoint = {
    x: bc.x + ax * faceDims.width,
    y: bc.y + ay * faceDims.height,
    z: bc.z + az * faceDims.depth,
  };

  const rotatedPoint = rotatePoint(localPoint, rotation);

  return {
    x: pivotPos.x + rotatedPoint.x,
    y: pivotPos.y + rotatedPoint.y,
    z: pivotPos.z + rotatedPoint.z,
  };
}

// Alias historique conserve pour la retro-compat dans le renderer.
export const getWorldAnchorPosition = getAnchorPosition;

// =============================================================================
// RESOLUTION DES POSITIONS — depuis dimensions produit uniquement
// =============================================================================

/**
 * Resout les positions de toutes les pieces.
 *
 * @param {Array}  parts       — liste des parts (avec .anchor, .rotationX/Y/Z, .resizeAxisX/Y, .name/.clientId/.id)
 * @param {Object} productSize — { x, y, z } dimensions produit (x=profondeur, y=largeur, z=hauteur)
 * @param {Object} realMeshSizes — OPTIONNEL { [partId]: { width, height, depth, bboxCenter } } vraies dimensions des meshes
 * @returns {Object} { [partId]: { x, y, z } }
 */
export function resolvePositions(parts, productSize, realMeshSizes = null) {
  const positions = {};
  const resolved = new Set();
  // Spécificité renderer : on stringifie les IDs pour matcher targetPartId
  // qui arrive du backend public en number ou en string selon les routes.
  const byId = new Map(parts.map(p => [String(p.clientId || p.id), p]));

  const dims = {};
  for (const p of parts) {
    const pid = String(p.clientId || p.id);
    if (realMeshSizes && realMeshSizes[pid]) {
      const meshData = realMeshSizes[pid];
      dims[pid] = {
        width: meshData.width,
        height: meshData.height,
        depth: meshData.depth,
        bboxCenter: meshData.bboxCenter || { x: 0, y: 0, z: 0 },
      };
    } else {
      dims[pid] = getFaceDimensions(p, productSize);
    }
  }

  const getRot = (p) => ({ x: p.rotationX ?? 0, y: p.rotationY ?? 0, z: p.rotationZ ?? 0 });

  // Toutes les pièces sans anchor sont des racines à (0,0,0)
  for (const part of parts) {
    const pid = String(part.clientId || part.id);
    if (!part.anchor) {
      positions[pid] = { x: 0, y: 0, z: 0 };
      resolved.add(pid);
    }
  }

  // Resolution iterative des pièces ancrées
  for (let i = 0; i < parts.length * 2 && resolved.size < parts.length; i++) {
    for (const part of parts) {
      const pid = String(part.clientId || part.id);
      if (resolved.has(pid)) continue;

      if (!part.anchor) {
        positions[pid] = { x: 0, y: 0, z: 0 };
        resolved.add(pid);
        continue;
      }

      const anchor = part.anchor;
      const targetId = String(anchor.targetPartId);
      if (!resolved.has(targetId)) continue;

      const target = byId.get(targetId);
      if (!target) continue;

      const myDims = dims[pid];
      const targetDims = dims[targetId];

      const targetAnchor = getAnchorPosition(anchor.targetPoint, targetDims, positions[targetId], getRot(target));
      const myOffset = getAnchorPosition(anchor.myPoint, myDims, { x: 0, y: 0, z: 0 }, getRot(part));

      const gap = anchor.gap || { x: 0, y: 0, z: 0 };

      positions[pid] = {
        x: targetAnchor.x - myOffset.x + (gap.x || 0),
        y: targetAnchor.y - myOffset.y + (gap.y || 0),
        z: targetAnchor.z - myOffset.z + (gap.z || 0),
      };
      resolved.add(pid);
    }
  }

  // Non resolues -> (0,0,0)
  for (const p of parts) {
    const pid = String(p.clientId || p.id);
    if (!positions[pid]) positions[pid] = { x: 0, y: 0, z: 0 };
  }

  return positions;
}

// Aliases historiques conserves pour la retro-compat (importes par d'autres modules).
export const resolveAnchoredPositions = (parts, realMeshSizes, productSize) => resolvePositions(parts, productSize, realMeshSizes);
export const resolveWorldAnchoredPositions = resolveAnchoredPositions;

// =============================================================================
// UTILITAIRES
// =============================================================================

export function createAnchor(myPoint, targetPartId, targetPoint, gap = { x: 0, y: 0, z: 0 }) {
  return {
    id: `anchor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    myPoint, targetPartId, targetPoint,
    gap: { x: gap.x || 0, y: gap.y || 0, z: gap.z || 0 },
  };
}

export function wouldCreateLoop(parts, fromId, toId) {
  const visited = new Set();
  const map = new Map(parts.map(p => [String(p.clientId || p.id), p]));
  const visit = (id) => {
    if (String(id) === String(fromId)) return true;
    const sid = String(id);
    if (visited.has(sid)) return false;
    visited.add(sid);
    return map.get(sid)?.anchors?.some(a => visit(a.targetPartId)) ?? false;
  };
  return visit(toId);
}

export function getAvailableTargets(parts, currentId) {
  return parts.filter(p => {
    const pid = String(p.clientId || p.id);
    return pid !== String(currentId) && p.enabled !== false && !p._deleted && !wouldCreateLoop(parts, currentId, pid);
  });
}

export function getAnchorColor(index) {
  return ['#ef4444','#f97316','#eab308','#22c55e','#14b8a6','#3b82f6','#8b5cf6','#ec4899'][index % 8];
}
